/** Job history page with filtering, selection, and compare. */

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, TextField, MenuItem, IconButton, CircularProgress,
  Checkbox, Button,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ReplayIcon from "@mui/icons-material/Replay";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import { useJobs } from "@/hooks/useJobs";
import StatusChip from "@/components/StatusChip";

export default function Jobs() {
  const [statusFilter, setStatusFilter] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const { data, isLoading } = useJobs(statusFilter ? { status: statusFilter } : undefined);
  const navigate = useNavigate();

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  if (isLoading) return <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}><CircularProgress /></Box>;

  const jobs = data?.jobs ?? [];
  const canCompare = selected.size >= 2 && selected.size <= 4;

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h4">Jobs</Typography>
        <Button
          variant="contained"
          startIcon={<CompareArrowsIcon />}
          disabled={!canCompare}
          onClick={() => navigate(`/compare?ids=${[...selected].join(",")}`)}
        >
          Compare Selected ({selected.size})
        </Button>
      </Box>
      <Box sx={{ mb: 2 }}>
        <TextField select label="Status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} size="small" sx={{ minWidth: 150 }}>
          <MenuItem value="">All</MenuItem>
          <MenuItem value="pending">Pending</MenuItem>
          <MenuItem value="running">Running</MenuItem>
          <MenuItem value="complete">Complete</MenuItem>
          <MenuItem value="failed">Failed</MenuItem>
        </TextField>
      </Box>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" />
              <TableCell>Date</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Plugin</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {jobs.map((job) => (
              <TableRow key={job.jobId} hover>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={selected.has(job.jobId)}
                    onChange={() => toggle(job.jobId)}
                    disabled={!selected.has(job.jobId) && selected.size >= 4}
                  />
                </TableCell>
                <TableCell>{new Date(job.createdAt).toLocaleString()}</TableCell>
                <TableCell>{job.name || job.pluginName || job.pluginId}</TableCell>
                <TableCell>{job.pluginName ?? job.pluginId}</TableCell>
                <TableCell>{job.jobType}</TableCell>
                <TableCell><StatusChip status={job.status} /></TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => navigate(`/jobs/${job.jobId}`)}><VisibilityIcon fontSize="small" /></IconButton>
                  <IconButton size="small" onClick={() => navigate(`/plugins/${job.pluginId}`)}><ReplayIcon fontSize="small" /></IconButton>
                </TableCell>
              </TableRow>
            ))}
            {jobs.length === 0 && (
              <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4 }}>No jobs found.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
